import { SanitizedHtml } from '../components/SanitizedHtml';
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { legalPagesApi, type FAQPageContent } from '../services/legalPagesApi';
import { fallbackFAQData } from '../data/legalPagesData';
import './FAQPage.css';

const FAQPage: React.FC = () => {
  const location = useLocation();
  const [faqData, setFaqData] = useState<FAQPageContent | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Scroll to top when location changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Zeitüberschreitung beim Laden der Inhalte.')), 10000)
        );
        
        const apiPromise = legalPagesApi.getFAQContent();
        
        const content = await Promise.race([apiPromise, timeoutPromise]) as FAQPageContent;
        setFaqData(content);
      } catch (err) {
        setFaqData(fallbackFAQData);
        console.error('FAQ-Inhalte konnten nicht geladen werden:', err);
        setError('Die Inhalte konnten nicht geladen werden.');
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  const toggleItem = (itemId: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  if (isLoading) {
    return (
      <div className="faq-loading">
        <div className="container">
          <h2 className="faq-loading-text">Lade Inhalte...</h2>
        </div>
      </div>
    );
  }

  if (error || !faqData) {
    return (
      <div className="faq-loading">
        <div className="container">
          <h2 className="faq-loading-text">FAQ</h2>
          <p>{error || 'Inhalte können nicht geladen werden'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="faq-page">
      <div className="container">
        {/* Header */}
        <div className="faq-header">
          <h1 className="faq-title">{faqData.title}</h1>
          {faqData.subtitle && (
            <p className="faq-subtitle">{faqData.subtitle}</p>
          )}
        </div>

        {/* FAQ Categories */}
        <div className="faq-categories">
          {faqData.categories.map((category) => (
            <div key={category.name} className="faq-category">
              <div className="faq-category-header">
                <h2 className="faq-category-title">{category.name}</h2>
              </div>
              
              <div className="faq-items">
                {category.items.map((item) => (
                  <div key={item.id} className="faq-item">
                    <button
                      onClick={() => toggleItem(item.id)}
                      className="faq-question-button"
                    >
                      <h3 className="faq-question">
                        {item.question}
                      </h3>
                      <span className={`faq-toggle ${expandedItems.has(item.id) ? 'expanded' : ''}`}>
                        +
                      </span>
                    </button>
                    
                    {expandedItems.has(item.id) && (
                      <div className="faq-answer">
                        <SanitizedHtml className="faq-answer-content" content={item.answer} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Contact Info */}
        {faqData.contactInfo && (
          <div className="faq-contact">
            <h2 className="faq-contact-title">{faqData.contactInfo.title}</h2>
            <p className="faq-contact-description">{faqData.contactInfo.description}</p>
            
            <div className="faq-contact-info">
              <div className="faq-contact-item">
                <span className="faq-contact-label">E-Mail:</span>
                <a href={`mailto:${faqData.contactInfo.email}`} className="faq-contact-link">
                  {faqData.contactInfo.email}
                </a>
              </div>
              {faqData.contactInfo.phone && (
                <div className="faq-contact-item">
                  <span className="faq-contact-label">Telefon:</span>
                  <a href={`tel:${faqData.contactInfo.phone}`} className="faq-contact-link">
                    {faqData.contactInfo.phone}
                  </a>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer Note */}
        <div className="faq-footer">
          Stand: {new Date(faqData.generatedAt).toLocaleDateString('de-DE')}
        </div>
      </div>
    </div>
  );
};

export default FAQPage;
